import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Clock, Sparkles, Target, Users } from "lucide-react";

import { Breadcrumbs } from "@/components/breadcrumbs";
import { Button } from "@/components/ui/button";
import { SiteHeader, SiteFooter } from "@/components/site-chrome";
import { MobileCtaBar } from "@/components/landing/mobile-cta-bar";
import { getProgramme } from "@/content/programmes";
import { breadcrumbJsonLd, SITE_URL, BRAND_OG_IMAGE, BRAND_OG_IMAGE_ALT, SITE_NAME } from "@/lib/seo";

export const Route = createFileRoute("/programmes/$slug")({
  loader: ({ params }) => {
    const programme = getProgramme(params.slug);
    if (!programme) throw notFound();
    return { programme };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Programme not found" }, { name: "robots", content: "noindex" }],
      };
    }
    const p = loaderData.programme;
    const url = `${SITE_URL}/programmes/${p.slug}`;
    return {
      meta: [
        { title: p.metaTitle },
        { name: "description", content: p.metaDescription },
        { property: "og:title", content: p.metaTitle },
        { property: "og:description", content: p.metaDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: SITE_NAME },
        { property: "og:image", content: BRAND_OG_IMAGE },
        { property: "og:image:alt", content: BRAND_OG_IMAGE_ALT },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: BRAND_OG_IMAGE },
        { name: "twitter:image:alt", content: BRAND_OG_IMAGE_ALT },
        { name: "twitter:title", content: p.metaTitle },
        { name: "twitter:description", content: p.metaDescription },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbJsonLd([
          { name: "Programmes", path: "/programmes" },
          { name: p.title, path: `/programmes/${p.slug}` },
        ]),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            name: p.title,
            description: p.summary,
            url,
            educationalLevel: `CEFR ${p.cefr}`,
            typicalAgeRange: p.ageRange === "0-99" ? undefined : p.ageRange,
            inLanguage: "en",
            teaches: p.outcomes.join("; "),
            provider: { "@id": `${SITE_URL}/#organization` },
            hasCourseInstance: {
              "@type": "CourseInstance",
              courseMode: "Online",
              courseWorkload: p.workload,
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: ProgrammeNotFound,
  component: ProgrammeDetail,
});

function ProgrammeNotFound() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 py-20 text-center">
        <h1 className="text-3xl text-primary">Programme not found</h1>
        <p className="mt-3 text-muted-foreground">
          That programme does not exist. Browse all programmes instead.
        </p>
        <Button variant="hero" className="mt-6 rounded-lg" asChild>
          <Link to="/programmes">View all programmes</Link>
        </Button>
      </main>
      <SiteFooter />
    </div>
  );
}

function ProgrammeDetail() {
  const { slug } = Route.useParams();
  const p = getProgramme(slug)!;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <Breadcrumbs
        trail={[
          { name: "Programmes", path: "/programmes" },
          { name: p.title, path: `/programmes/${p.slug}` },
        ]}
      />

      <main className="pb-20 lg:pb-0">
        <section className="mx-auto max-w-4xl px-5 py-12">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary-foreground">
              {p.band}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent">
              <span className="size-1.5 animate-pulse rounded-full bg-accent" />
              {p.spots}
            </span>
          </div>

          <h1 className="mt-5 text-4xl leading-tight text-primary sm:text-5xl">{p.title}</h1>
          <p className="mt-5 text-lg leading-relaxed text-muted-foreground">{p.summary}</p>
          <p className="mt-4 leading-relaxed text-muted-foreground">{p.intro}</p>

          <dl className="mt-8 grid gap-4 sm:grid-cols-3">
            <div className="surface-card p-5">
              <Target className="size-5 text-accent" />
              <dt className="mt-2 text-xs font-bold uppercase tracking-wider text-primary">
                CEFR alignment
              </dt>
              <dd className="mt-1 text-sm text-muted-foreground">CEFR {p.cefr}</dd>
            </div>
            <div className="surface-card p-5">
              <Clock className="size-5 text-accent" />
              <dt className="mt-2 text-xs font-bold uppercase tracking-wider text-primary">
                Lesson length
              </dt>
              <dd className="mt-1 text-sm text-muted-foreground">{p.duration}</dd>
            </div>
            <div className="surface-card p-5">
              <Users className="size-5 text-accent" />
              <dt className="mt-2 text-xs font-bold uppercase tracking-wider text-primary">
                Cohort size
              </dt>
              <dd className="mt-1 text-sm text-muted-foreground">
                {p.cohort}
                {p.cohort !== "One-on-one" ? " · 80%+ speaking time" : ""}
              </dd>
            </div>
          </dl>

          <h2 className="mt-12 text-2xl text-primary">Learning outcomes</h2>
          <ul className="mt-4 space-y-3 leading-relaxed text-muted-foreground">
            {p.outcomes.map((o) => (
              <li key={o} className="flex gap-2.5">
                <Sparkles className="mt-1 size-4 shrink-0 text-accent" />
                <span>{o}</span>
              </li>
            ))}
          </ul>

          <h2 className="mt-12 text-2xl text-primary">Timetable</h2>
          <p className="mt-3 leading-relaxed text-muted-foreground">
            {p.cohort === "One-on-one"
              ? "One-on-one slots are scheduled around your availability and target exam date, four times per week. Your exact schedule is confirmed during the free assessment."
              : "Lessons run at 9, 10 and 11am and 2, 3, 4, 5 and 6pm Toronto time (ET), two or four times per week depending on your plan. Your exact slot is confirmed during the free assessment."}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3">
            <Button variant="hero" size="xl" asChild>
              <Link to="/book" aria-label={`Book a free English assessment for ${p.title}`}>
                Book Free Assessment
              </Link>
            </Button>
            <Link
              to="/pricing"
              className="group inline-flex items-center gap-2 text-base font-semibold text-primary transition-colors hover:text-accent"
            >
              See enrolment pricing
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <p className="mt-10">
            <Link
              to="/programmes"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-accent"
            >
              <ArrowLeft className="size-4 transition-transform group-hover:-translate-x-1" />
              Back to all programmes
            </Link>
          </p>
        </section>
      </main>

      <SiteFooter />
      <MobileCtaBar />
    </div>
  );
}
